/**
 * ControlCenterLinksSection 產品管控中心 (SR3.5, made EXECUTABLE by S6.2) — the
 * DD!183–187 quintet honored LITERALLY as five FILTERED DEEP-LINKS into
 * existing surfaces, with ZERO new subtabs and ZERO new grids (Appendix D; all
 * three plan drafts agreed).
 *
 * S6.2 (plan §S6.2): the xlsx col-J instruction 產品管控中心應該查得到 ×5
 * (DD!183 Items On BO · DD!184 Items on Order · DD!185 Projected Demand ·
 * DD!186 Projected Supply · DD!187 Open SO) is carried as DATA —
 * `CONTROL_CENTER_DD_ROWS` maps every link key to its provenance row, and the
 * component renders from the SAME `buildControlCenterLinks` /
 * `controlCenterLabelKey` builders that `__tests__/controlCenterVisibility.
 * test.tsx` executes (all 5 labels must resolve en+zh). The S8.2 closure table
 * credits these 5 rows to this section via the exported map.
 *
 * Every link carries the canonical `?productId=<internalProductId>` scope that
 * SR2.2 wired into the OCC lenses / SO list, so the target lands already
 * filtered to this product. No figure is recomputed here — the back-order total
 * reached via the Items-On-BO link is the SAME `selectOrderBalanceTotal` the OCC
 * 待出量 uses (the W11 single-balance-source rule), so link === OCC 待出量 by
 * construction rather than by a second computation.
 *
 * Projected-Supply points at the EXISTING open-PO / OCC surface exactly like the
 * other quintet links — the net-new running-balance PAB projection (SR3.6) is
 * DEFERRED to v1.5 (OQ-17), so in v1 it deliberately shares the open-PO landing
 * with Items-on-Order.
 *
 * Self-contained: drop into the detail-page Sider (SR4.1 wiring) with a single
 * `<ControlCenterLinksSection product={product} />` line.
 */

import { Card, Col, Row, Typography, theme } from 'antd';
import {
  ExceptionOutlined,
  InboxOutlined,
  ProfileOutlined,
  RightOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { radiusTokens } from '@/styles/tokens';
import type { Product } from '@/types/api/product';
import SectionHeader from './SectionHeader';

const { Text } = Typography;

/* eslint-disable react-refresh/only-export-components -- the DD-row map and
   the link/label builders must live in the SAME module as the section that
   renders them (registry-as-data; the S6.2 executable-visibility contract is
   asserted by __tests__/controlCenterVisibility.test.tsx). */

/**
 * DD row (0708 xlsx col J, 產品管控中心應該查得到) per link key — the
 * registry-as-data disposition of the quintet. Key order == render order ==
 * DD-row order (the Item360-subtab sequence the xlsx walks, 183→187).
 * S6.2 executable-visibility contract: `controlCenterVisibility.test.tsx`
 * fails on a missing / double-claimed row or an unresolvable label.
 */
export const CONTROL_CENTER_DD_ROWS = {
  itemsOnBo: 183,
  itemsOnOrder: 184,
  projectedDemand: 185,
  projectedSupply: 186,
  openSo: 187,
} as const;

export type ControlCenterLinkKey = keyof typeof CONTROL_CENTER_DD_ROWS;

export const CONTROL_CENTER_LINK_KEYS = Object.keys(
  CONTROL_CENTER_DD_ROWS
) as readonly ControlCenterLinkKey[];

/** i18n label key per link — the exact key the card title renders with. */
export const controlCenterLabelKey = (key: ControlCenterLinkKey): string =>
  `products:controlCenter.links.${key}`;

/** i18n hint key per link — the exact key the card subtitle renders with. */
export const controlCenterHintKey = (key: ControlCenterLinkKey): string =>
  `products:controlCenter.linkHints.${key}`;

export interface ControlCenterDeepLink {
  key: ControlCenterLinkKey;
  /** Provenance row in the 0708 xlsx (col-J 產品管控中心應該查得到). */
  ddRow: number;
  /** Deep-link destination, already scoped to this product via `?productId=`. */
  to: string;
}

/**
 * Pure deep-link builder (node-testable — no DOM, no hooks). The canonical
 * business identity the OCC lenses / SO list / BO report scope on — SR2.2
 * reads `?productId=` as the internalProductId, never the DB id.
 */
export const buildControlCenterLinks = (internalProductId: string): ControlCenterDeepLink[] => {
  const pid = encodeURIComponent(internalProductId);

  const destinations: Record<ControlCenterLinkKey, string> = {
    // DD!183 欠交 → back-order report, product-scoped (shares
    // selectOrderBalanceTotal, W11).
    itemsOnBo: `/auth/reports/back-order?productId=${pid}`,
    // DD!184 採購在途 → OCC 訂單明細 open-PO lines, product-scoped. `view` is an
    // intent discriminator so this and 預計供給 don't resolve to an identical
    // URL (the lens ignores unknown `view` values today; it lets the future PAB
    // view branch without re-touching this file — SR3.6 / OQ-17).
    itemsOnOrder: `/auth/occ/lines?productId=${pid}&view=open-po`,
    // DD!185 預計需求 → demand pool, product-scoped.
    projectedDemand: `/auth/purchase-orders/demand-pool?productId=${pid}`,
    // DD!186 預計供給 (在途量) → the EXISTING open-PO / OCC surface (SR3.6 PAB
    // deferred, OQ-17), so it shares the open-PO landing with Items-on-Order by
    // design in v1. Its `view=projected-supply` intent keeps the deep-link
    // distinct from Items-on-Order and pre-labels where the future PAB
    // projection will branch.
    projectedSupply: `/auth/occ/lines?productId=${pid}&view=projected-supply`,
    // DD!187 未結銷售單 → SO list, open-only + product-scoped (SR2.2 openOnly
    // modifier).
    openSo: `/auth/sales-orders?productId=${pid}&openOnly=1`,
  };

  return CONTROL_CENTER_LINK_KEYS.map((key) => ({
    key,
    ddRow: CONTROL_CENTER_DD_ROWS[key],
    to: destinations[key],
  }));
};

/** Card icon per link key (presentation-only — stays out of the pure builder). */
const LINK_ICONS: Record<ControlCenterLinkKey, React.ReactNode> = {
  itemsOnBo: <ExceptionOutlined />,
  itemsOnOrder: <ShoppingCartOutlined />,
  projectedDemand: <RiseOutlined />,
  projectedSupply: <InboxOutlined />,
  openSo: <ProfileOutlined />,
};

interface ControlCenterLinksSectionProps {
  product: Product;
}

export const ControlCenterLinksSection = ({ product }: ControlCenterLinksSectionProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const links = buildControlCenterLinks(product.internalProductId);

  const openLink = (to: string) => navigate(to);

  return (
    <div>
      <SectionHeader
        title={t('products:controlCenter.title')}
        subtitle={t('products:controlCenter.subtitle')}
      />
      <Row gutter={[16, 16]}>
        {links.map((link) => (
          <Col key={link.key} xs={24} sm={12} xl={8}>
            <Card
              size="small"
              hoverable
              role="link"
              tabIndex={0}
              aria-label={`${t(controlCenterLabelKey(link.key))} — ${t(
                'products:controlCenter.openFiltered'
              )}`}
              style={{ cursor: 'pointer', height: '100%' }}
              onClick={() => openLink(link.to)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openLink(link.to);
                }
              }}
            >
              <div className="tw-flex tw-items-start tw-gap-3">
                <div
                  aria-hidden
                  className="tw-flex tw-items-center tw-justify-center tw-shrink-0"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: radiusTokens.card,
                    backgroundColor: token.colorPrimaryBg,
                    color: token.colorPrimary,
                    fontSize: token.sizeMD,
                  }}
                >
                  {LINK_ICONS[link.key]}
                </div>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                    <span style={{ fontWeight: token.fontWeightStrong }}>
                      {t(controlCenterLabelKey(link.key))}
                    </span>
                    <RightOutlined
                      aria-hidden
                      style={{ color: token.colorTextTertiary, fontSize: token.sizeXS }}
                    />
                  </div>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: token.fontSizeSM,
                      display: 'block',
                      marginTop: token.marginXXS,
                    }}
                  >
                    {t(controlCenterHintKey(link.key))}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default ControlCenterLinksSection;
